'use client';

import Grid from "@mui/material/Grid2";
import { Box, Container, Divider, Typography } from "@mui/material";
import BorderGradientBox from "@/src/components/common/border-gradient-box/border-gradient-box";
import Image from "next/image";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/pro-duotone-svg-icons";
import ArticleOutlines from "@/src/components/journal-site/articles/article-outlines/article-outlines";
import ArticleDoi from "@/src/components/journal-site/articles/article-doi/article-doi";
import ReactHtmlParser from "react-html-parser";
import ArticleAuthors from "@/src/components/journal-site/articles/article-authors/article-authors";
import AuthorAffiliation from "@/src/components/journal-site/articles/author-affiliation/author-affiliation";
import AuthorCorrespondence from "@/src/components/journal-site/articles/author-correspondence/author-correspondence";
import ArticleMetaDataComponent from "@/src/components/journal-site/articles/article-meta-data/article-meta-data";
import HowToCite from "@/src/components/journal-site/articles/how-to-cite/how-to-cite";
import PageContent from "@/src/components/journal-site/common/page-content/page-content";
import ArticleAbstract from "@/src/components/journal-site/articles/article-abstract/article-abstract";
import ArticleKeywords from "@/src/components/common/keywords/keywords";
import ArticleBody from "@/src/components/journal-site/articles/article-body/article-body";
import ArticleAcknowledgments
    from "@/src/components/journal-site/articles/article-acknowledgments/article-acknowledgments";
import ArticleFootnotes from "@/src/components/journal-site/articles/article-footnotes/article-footnotes";
import ArticleReferences from "@/src/components/journal-site/articles/article-references/article-references";
import ArticleCopyRight from "@/src/components/journal-site/articles/article-copyright/article-copyright";
import CommentsComponent from "@/src/components/common/comments/comment-form";
import ArticleRelatedArticles from "@/src/components/journal-site/articles/article-related-articles";
import ArticleSidebar from "@/src/components/journal-site/articles/article-sidebar/article-sidebar";
import type Article from "@/src/_types/Article/article";
import type ArticleBodyProps from "@/src/_types/Article/article-body";
import type ArticleFiles from "@/src/_types/Article/article-files";
import Author from "@/src/_types/Authors/author";
import Journal from "@/src/_types/journal";
import CitationData from "@/src/_types/Article/article-citation-data";

export default function ArticleComponent({
                                             journal,
                                             document,
                                             citation,
                                             article,
                                         }: {
    journal: Journal;
    document: Article;
    citation: CitationData[];
    article: string;
}) {
    return (
        <Container
            sx={{
                paddingLeft: {
                    xs: 1,
                    sm: 1.5,
                    md: 1.5,
                    lg: 0
                },
                paddingRight: {
                    xs: 1,
                    sm: 1.5,
                    md: 1.5,
                    lg: 0
                },
                maxWidth: '100% !important'
            }}
        >
            <Grid
                container
                spacing={2}
                py={{
                    xs: 2,
                    sm: 2,
                    md: 4
                }}
            >
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 2
                    }}
                    display={{
                        xs: 'none',
                        sm: 'none',
                        md: 'none',
                        lg: 'block'
                    }}
                >
                    <Box
                        position="sticky"
                        top={140}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {
                            document?.feature_image
                                ? <Box
                                    width="100%"
                                    mb={{ xs: 0, sm: 0, md: 1.5 }}
                                >
                                    <BorderGradientBox size={5}>
                                        <Image
                                            src={process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE + document?.feature_image}
                                            alt={document?.feature_image_caption || ''}
                                            width={200}
                                            height={200}
                                            quality={100}
                                            style={{
                                                width: '100%',
                                                height: 'auto'
                                            }}
                                        />
                                    </BorderGradientBox>
                                    <Typography pt={.25} sx={{
                                        wordBreak: 'break-all'
                                    }}>
                                        <SpanTypography fontSize={'.8rem'} mr={.25}>
                                            Image Credit:
                                        </SpanTypography>
                                        <SpanTypography fontSize={'.8rem'}>
                                            {
                                                document?.feature_image_caption
                                            }
                                        </SpanTypography>
                                    </Typography>
                                </Box>
                                : <Typography
                                    component="div"
                                    fontSize={175}
                                    lineHeight={0}
                                    mb={{ xs: 0, sm: 0, md: 1.5 }}
                                >
                                    <FontAwesomeIcon icon={faFileLines} />
                                </Typography>
                        }
                        <Box width="100%">
                            <ArticleOutlines
                                hasAbstract={document?.abstract && document?.abstract.length > 0}
                                hasAcknowledgments={document?.acknowledgments && document?.acknowledgments.length > 0}
                                hasFootnotes={document?.footnotes && document?.footnotes.length > 0}
                                hasReferences={document?.references && document?.references.length > 0}
                                hasCopyright={document?.copyright !== ''}
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid size={{
                    xs: 12,
                    sm: 12,
                    md: 12,
                    lg: 8
                }}>
                    <Box>
                        <Box>
                            {
                                document?.doi &&
                                <ArticleDoi
                                    articleDoi={document?.doi}
                                />
                            }
                            <Typography
                                variant="h1"
                                color="primary.main"
                                fontSize={'1.75rem'}
                                mb={1}
                            >
                                {
                                    ReactHtmlParser(document?.title || '')
                                }
                            </Typography>
                            <ArticleAuthors
                                authors={document?.authors || []}
                            />
                            {
                                (
                                    document?.affiliations
                                    && document?.affiliations.length > 0
                                ) &&
                                <Box id="authors-affiliations" mb={.5}>
                                    <AuthorAffiliation
                                        affiliations={document?.affiliations || []}
                                    />
                                </Box>
                            }
                            {
                                (
                                    document?.correspondences
                                    && document?.correspondences.length > 0
                                ) &&
                                <Box id="article-correspondences">
                                    <AuthorCorrespondence
                                        correspondences={document?.correspondences || []}
                                        hasEqualContributer={
                                            document?.authors.some(
                                                (author: Author) =>
                                                    author.equal_contrib === 'yes'
                                            ) || false
                                        }
                                        isDeceased={
                                            document?.authors.some(
                                                (author: Author) =>
                                                    author['deceased'] !== ''
                                            ) || false
                                        }
                                        authorNoteFnEqual={
                                            document
                                                ? document.author_note_fn_equal
                                                : ''
                                        }
                                    />
                                </Box>
                            }
                            <Divider
                                style={{
                                    marginTop: '1rem',
                                    marginBottom: '1rem',
                                    borderColor: 'custom.dividerBorderColor',
                                    opacity: .25
                                }}
                            />
                            <ArticleMetaDataComponent
                                journalTitle={journal.title || ''}
                                articleMetaData={document}
                                issueUrl={`/journals/${journal.path}/issue/${document.issue_id}`}
                            />
                            {
                                (
                                    document.related_articles.length > 0 ||
                                    document.source_related_articles.length > 0
                                ) &&
                                <ArticleRelatedArticles
                                    relatedArticles={document.related_articles || []}
                                    sourceRelatedArticles={document.source_related_articles || []}
                                />
                            }
                            <HowToCite
                                howTociteDictionary="How to Cite:"
                                data={document}
                                journal={journal}
                            />
                        </Box>
                        {
                            (
                                document?.abstract
                                && document?.abstract.length > 0
                            ) &&
                            <PageContent mb={.5}>
                                <Box id="article-abstract">
                                    <ArticleAbstract
                                        abstracts={document?.abstract}
                                    />
                                </Box>
                            </PageContent>
                        }

                        {
                            (
                                document?.keywords
                                && document?.keywords?.length > 0
                            ) &&
                            <PageContent mb={.5}>
                                <ArticleKeywords
                                    keywords={document?.keywords}
                                />
                            </PageContent>
                        }
                        {((document?.body && document?.body.sections.length > 0) ||
                                (document?.acknowledgments && document.acknowledgments.length > 0) ||
                                (document?.footnotes && document.footnotes.length > 0)) &&
                            <PageContent mb={.5}>
                                {
                                    (
                                        document?.body
                                        && document.body.sections.length > 0
                                    ) &&
                                    <Box id="article-body">
                                        <ArticleBody
                                            articleBody={document?.body?.sections}
                                        />
                                    </Box>
                                }
                                {
                                    (
                                        document?.acknowledgments
                                        && document.acknowledgments.length > 0
                                    ) &&
                                    <Box id="article-acknowledgments">
                                        <ArticleAcknowledgments
                                            acknowledgments={document?.acknowledgments}
                                        />
                                    </Box>
                                }
                                {
                                    (
                                        document?.footnotes
                                        && document.footnotes.length > 0
                                    ) &&
                                    <Box id="article-footnotes">
                                        <ArticleFootnotes
                                            footnotes={document?.footnotes}
                                        />
                                    </Box>
                                }
                            </PageContent>}
                        {
                            (
                                document?.references
                                && document.references.length > 0
                            ) &&
                            <PageContent mb={.5}>
                                <Box id="article-references">
                                    <ArticleReferences
                                        references={document?.references}
                                    />
                                </Box>
                            </PageContent>
                        }
                        {
                            (
                                document?.copyright
                                && document.copyright !== ''
                            ) &&
                            <PageContent>
                                <Box id="article-copyright">
                                    <ArticleCopyRight
                                        copyright={document?.copyright}
                                    />
                                </Box>
                            </PageContent>
                        }
                        <CommentsComponent
                            url={`${journal.slug}/en/document/comment/publish/add/${document?.id}`}
                            comments={document?.comments || []}
                            bgColor="#ebe7dd"
                        />
                    </Box>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 2
                    }}
                >
                    <ArticleSidebar
                        data={document as Article & ArticleBodyProps & ArticleFiles}
                        articleUrl={{
                            path: `/articles/${article}`,
                            slug: article
                        }}
                        citation={citation}
                        statement={journal.options?.template_configs?.article_sidebar?.statement}
                        eISSN={journal.meta_data?.issn_epub || ''}
                        hasPlumx={journal.services?.plumix_metrics?.status}
                        hasScoupos={!!journal.meta_data?.citation_score}
                    />
                </Grid>
            </Grid>
        </Container>
    )
}
